import { Text, useRouter } from '@capsicum/common-web';
import { useNCWSendAssetSelector } from '@capsicum/features/nc-wallet/shared';
import { useNCWBalance } from '@capsicum/socios-core';
import { useCallback, useMemo } from 'react';

import { useV2MigrationStatus } from '../../../hooks';
import { useV2FTSupportedDestinationsWarningModal } from '../../../hooks/useV2FTSupportedDestinationsWarningModal';
import { NCWAssetSelectionButtonSection, NCWSendAssetCard, NCWSendAssetNoSearchResults } from '../../../ui';

import { CardSkeletons } from './CardSkeletons';
import { NoAssetsGoat } from './NoAssetsGoat';
import * as S from './styled';

/**
 * This is the first page of the NCW Send flow
 */
export const NCWSendAssetSelector = () => {
	const { push } = useRouter();
	const { chilizBalance, isFetching, isPending, tokenBalances } = useNCWBalance();
	const { isLoading: isV2MigrationStatusLoading, isMigrating } = useV2MigrationStatus();

	const {
		chilizSearchResult,
		fanTokenSearchResults,
		foundFanTokensMatchingSearchKeyword,
		intlText,
		isLoading,
		onSearchChange,
		searchKeyword,
		sendAsset,
		setSendAsset
	} = useNCWSendAssetSelector(isPending || isV2MigrationStatusLoading, tokenBalances, chilizBalance, isMigrating);

	const { handleSubmit, isLoading: isV2SupportedDestinationsModalLoading } = useV2FTSupportedDestinationsWarningModal(
		{
			asset: sendAsset ?? undefined,
			onSubmit: useCallback(
				() =>
					sendAsset &&
					push(
						{
							chiliz: './send/chiliz',
							token: './send/fan-tokens'
						}[sendAsset.assetType]
					),

				[push, sendAsset]
			)
		}
	);

	const assetRows = useMemo(() => {
		const { asset: chilizAsset, show: showChiliz = false } = chilizSearchResult ?? {};
		const chilizRow = chilizAsset && (
			<S.CardContainer $show={showChiliz} key={chilizAsset.assetId}>
				<NCWSendAssetCard
					asset={chilizAsset}
					selected={sendAsset?.assetId === chilizAsset.assetId}
					testID={`ncw-send-${chilizAsset.assetName}-asset-card`}
					onSelectAsset={setSendAsset}
				/>
			</S.CardContainer>
		);

		const tokenRows =
			fanTokenSearchResults.map(({ asset, show }) => (
				<S.CardContainer $show={show} key={asset.assetId}>
					<NCWSendAssetCard
						asset={asset}
						selected={sendAsset?.assetId === asset.assetId}
						testID={`ncw-send-${asset.assetName}-asset-card`}
						onSelectAsset={setSendAsset}
					/>
				</S.CardContainer>
			)) ?? [];

		const tokensHeading = (
			<S.FanTokensTitleContainer $show={foundFanTokensMatchingSearchKeyword} key="tokensTitle">
				<Text variant="text5">{intlText.fanTokenListTitle}</Text>
			</S.FanTokensTitleContainer>
		);

		return [chilizRow, tokensHeading, ...tokenRows];
	}, [chilizSearchResult, fanTokenSearchResults, foundFanTokensMatchingSearchKeyword, sendAsset]);

	const handleSearch = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			onSearchChange(event.target.value);
		},
		[onSearchChange]
	);

	let assetListContent;

	if (isLoading || isFetching) {
		assetListContent = <CardSkeletons />;
	} else if (searchKeyword && !foundFanTokensMatchingSearchKeyword && !chilizSearchResult?.show) {
		assetListContent = <NCWSendAssetNoSearchResults />;
	} else {
		assetListContent = assetRows;
	}

	const hasChiliz = (chilizBalance?.amount ?? 0) > 0;
	const hasTokens = (tokenBalances?.length ?? 0) > 0;

	if (!hasChiliz && !hasTokens && !isFetching && !isLoading) {
		return (
			<S.Wrapper>
				<NoAssetsGoat />
			</S.Wrapper>
		);
	}

	return (
		<S.Wrapper>
			<Text variant="title5">{intlText.pageTitle}</Text>
			<S.Search
				maxLength={16}
				placeholder={intlText.searchPlaceholder}
				testID="ncw-send-assets-searchInput"
				onChange={handleSearch}
			/>
			<S.AssetList>{assetListContent}</S.AssetList>

			<NCWAssetSelectionButtonSection
				isLoading={isV2SupportedDestinationsModalLoading}
				selectedAsset={sendAsset}
				submitButtonText={intlText.continueBtnText}
				onSubmit={handleSubmit}
			/>
		</S.Wrapper>
	);
};
